/**
 * Модель рабочего места проверяющего.
 *
 * Вынесена из разметки ради четырёх мест, где смысл теряется легче всего:
 *
 * * **исход проверки** — три значения, и ни одно не «нейтральнее» другого.
 *   Незнакомый код показывается незнакомым, а не подставляется ближайшим;
 * * **причина** — обязательна для спора и запроса уточнения. Правило живёт на
 *   сервере, но кнопка не должна отправлять заведомо отказной запрос: это
 *   мешает человеку, а не защищает данные;
 * * **роль проверяющего** — «проверил менеджер» и «проверил проверяющий» это
 *   разные утверждения о независимости, и подпись обязана их различать;
 * * **итог массового выезда** — «затронуто 3 из 27» без слова «почему»
 *   читается как сбой, а это три РАЗНЫЕ сработавшие причины.
 */

import type {
  BulkVerifyOut,
  VerificationClaimOut,
  VerificationQueueRowOut,
} from "../../api/client";
import { defineMessages, type PlainMessage } from "../../i18n/define";
import { tr } from "../../i18n/runtime";

const m = defineMessages({
  outConfirm: { id: 'canonVerification.outcome.confirmed', defaultMessage: 'Подтвердить', description: 'Вариант списка «Исход проверки» (инспектор, менеджер): отчёт и проверка совпадают; действие, коротко' },
  outClarify: { id: 'canonVerification.outcome.needsClarification', defaultMessage: 'Требует уточнения', description: 'Вариант списка «Исход проверки»: нужен вопрос к хозяйству, спора нет; коротко' },
  outReject: { id: 'canonVerification.outcome.rejected', defaultMessage: 'Опровергнуть', description: 'Вариант списка «Исход проверки»: заявленная работа не подтвердилась; действие, коротко' },
  pastConfirm: { id: 'canonVerification.outcomePast.confirmed', defaultMessage: 'подтверждено', description: 'Бейдж с вынесенным суждением проверяющего: подтверждено (прошедшее время)' },
  pastClarify: { id: 'canonVerification.outcomePast.needsClarification', defaultMessage: 'требует уточнения', description: 'Бейдж с вынесенным суждением проверяющего: требует уточнения' },
  pastReject: { id: 'canonVerification.outcomePast.rejected', defaultMessage: 'опровергнуто', description: 'Бейдж с вынесенным суждением проверяющего: опровергнуто' },
  hintConfirm: { id: 'canonVerification.outcomeHint.confirmed', defaultMessage: 'Отчёт и проверка говорят одно и то же.', description: 'Пояснение под формой суждения при выбранном исходе «Подтвердить»' },
  hintClarify: { id: 'canonVerification.outcomeHint.needsClarification', defaultMessage: 'Вопрос без спора: работа не отрицается, но принять её как есть нельзя. Нужна причина.', description: 'Пояснение под формой суждения при выбранном исходе «Требует уточнения»' },
  hintReject: { id: 'canonVerification.outcomeHint.rejected', defaultMessage: 'Спор: заявленная работа на выезде не подтвердилась. Отчёт при этом не стирается — обе записи остаются, и состояние станет «конфликт». Нужна причина.', description: 'Пояснение под формой суждения при выбранном исходе «Опровергнуть»' },
  noJudgement: { id: 'canonVerification.outcome.none', defaultMessage: 'нет суждения', description: 'Подпись исхода, когда суждения нет' },
  notChecked: { id: 'canonVerification.outcomePast.none', defaultMessage: 'не проверено', description: 'Подпись «Последнее суждение», когда проверки ещё не было' },
  unknownCode: { id: 'canonVerification.unknownCode', defaultMessage: 'неизвестно: {code}', description: 'Подпись значения, которого экран не знает; {code} — код от сервера' },
  roleVerifier: { id: 'canonVerification.role.verifier', defaultMessage: 'проверяющий', description: 'Роль вносившего проверку: проверяющий (инспектор)' },
  roleManager: { id: 'canonVerification.role.hokimiyatManager', defaultMessage: 'менеджер хокимията', description: 'Роль вносившего проверку: менеджер хокимията' },
  roleFarm: { id: 'canonVerification.role.farm', defaultMessage: 'хозяйство', description: 'Роль вносившего запись: хозяйство' },
  roleObserver: { id: 'canonVerification.role.observer', defaultMessage: 'наблюдатель', description: 'Роль вносившего запись: наблюдатель' },
  roleQInspector: { id: 'canonVerification.role.qInspector', defaultMessage: 'инспектор карантина', description: 'Роль вносившего запись: инспектор карантинной службы' },
  roleQOfficer: { id: 'canonVerification.role.qOfficer', defaultMessage: 'ответственный карантинной службы', description: 'Роль вносившего запись: ответственный карантинной службы' },
  roleNone: { id: 'canonVerification.role.none', defaultMessage: 'роль не указана', description: 'Подпись роли, когда в записи роль не указана' },
  bulkHead: { id: 'canonVerification.bulk.head', defaultMessage: 'Записано: {applied, plural, one {# контур} few {# контура} many {# контуров} other {# контура}} из {total}.', description: 'Итог массового выезда по хозяйству (инспектор); {applied} — сколько контуров записано, {total} — сколько всего' },
  bulkWithSkips: { id: 'canonVerification.bulk.withSkips', defaultMessage: '{head} {skips}.', description: 'Итог массового выезда с объяснением пропусков; {head} — первая фраза итога, {skips} — причины пропусков через «; »' },
  skipNoReport: { id: 'canonVerification.bulk.skipNoReport', defaultMessage: '{count} пропущено — там нет отчёта, и проверять нечего (это не «не выполнено»)', description: 'Причина пропуска в итоге массового выезда; {count} — число пропущенных контуров без отчёта' },
  skipDecided: { id: 'canonVerification.bulk.skipDecided', defaultMessage: '{count} пропущено — там уже вынесено другое суждение, и выезд с ним не спорит', description: 'Причина пропуска в итоге массового выезда; {count} — число контуров с уже вынесенным другим суждением' },
  skipSame: { id: 'canonVerification.bulk.skipSame', defaultMessage: '{count} пропущено — там уже сказано ровно это же', description: 'Причина пропуска в итоге массового выезда; {count} — число контуров с тем же суждением' },
  openWindow: { id: 'canonVerification.group.openWindow', defaultMessage: 'У {count, plural, one {# работы} few {# работ} many {# работ} other {# работ}} окно ещё не закончилось: нет отчёта — ещё не срок, а не «не сделано».', description: 'Пояснение под заголовком группы кампании в очереди проверки; {count} — сколько работ группы с незакрытым окном' },
});


/** Исходы в том порядке, в каком их предлагают человеку. */
export const OUTCOMES = ["confirmed", "needs_clarification", "rejected"] as const;

export type Outcome = (typeof OUTCOMES)[number];

const OUTCOME_LABELS: Record<string, PlainMessage> = {
  confirmed: m.outConfirm,
  needs_clarification: m.outClarify,
  rejected: m.outReject,
};

const OUTCOME_PAST: Record<string, PlainMessage> = {
  confirmed: m.pastConfirm,
  needs_clarification: m.pastClarify,
  rejected: m.pastReject,
};

const OUTCOME_HINTS: Record<string, PlainMessage> = {
  confirmed: m.hintConfirm,
  needs_clarification: m.hintClarify,
  rejected: m.hintReject,
};

/**
 * Исходы, которые без причины не являются проверкой. Правило то же, что в
 * `work.enums.OUTCOMES_REQUIRING_REASON` и в ограничении БД: «не сделано» и
 * «нужно уточнить» без «почему» не разбираются и не оспариваются.
 */
export const OUTCOMES_REQUIRING_REASON: ReadonlySet<string> = new Set([
  "rejected",
  "needs_clarification",
]);

export function outcomeLabel(code: string | null | undefined): {
  label: string;
  known: boolean;
} {
  if (!code) return { label: tr(m.noJudgement), known: true };
  const label = OUTCOME_LABELS[code];
  return label
    ? { label: tr(label), known: true }
    : { label: tr(m.unknownCode, { code }), known: false };
}

/** Подпись уже вынесенного суждения (прошедшее время). */
export function outcomePast(code: string | null | undefined): {
  label: string;
  known: boolean;
  attention: boolean;
} {
  if (!code) return { label: tr(m.notChecked), known: true, attention: false };
  const label = OUTCOME_PAST[code];
  if (!label) return { label: tr(m.unknownCode, { code }), known: false, attention: false };
  return {
    label: tr(label),
    known: true,
    attention: code === "rejected" || code === "needs_clarification",
  };
}

export function outcomeHint(code: string): string | null {
  const hint = OUTCOME_HINTS[code];
  return hint ? tr(hint) : null;
}

export function needsReason(outcome: string): boolean {
  return OUTCOMES_REQUIRING_REASON.has(outcome);
}

/**
 * Можно ли отправить суждение. Причина проверяется здесь ТОЛЬКО чтобы не
 * отправлять заведомо отказной запрос: право и правило живут на сервере, и
 * заблокированная кнопка защитой не считается.
 */
export function canSubmit(outcome: string, reason: string): boolean {
  return !needsReason(outcome) || reason.trim().length > 0;
}

const ROLE_LABELS: Record<string, PlainMessage> = {
  verifier: m.roleVerifier,
  hokimiyat_manager: m.roleManager,
  farm: m.roleFarm,
  observer: m.roleObserver,
  q_inspector: m.roleQInspector,
  q_officer: m.roleQOfficer,
};

export interface RoleView {
  label: string;
  known: boolean;
  /**
   * Проверку внёс НЕ проверяющий. Это не нарушение: менеджер вправе. Но это
   * другое утверждение о независимости, и подпись обязана его сохранить —
   * менеджер бывает автором проверяемого отчёта.
   */
  actingAsManager: boolean;
}

export function roleView(code: string | null | undefined): RoleView {
  if (!code) {
    return { label: tr(m.roleNone), known: false, actingAsManager: false };
  }
  const label = ROLE_LABELS[code];
  return {
    label: label ? tr(label) : tr(m.unknownCode, { code }),
    known: Boolean(label),
    actingAsManager: code === "hokimiyat_manager",
  };
}

/**
 * Что сделал массовый выезд — словами. Пропуск объясняется всегда и по каждой
 * из трёх причин отдельно: они означают разное и требуют разного.
 */
export function bulkVerifyNote(result: BulkVerifyOut): string {
  const parts: string[] = [];
  if (result.skipped_no_report > 0) {
    parts.push(tr(m.skipNoReport, { count: result.skipped_no_report }));
  }
  if (result.skipped_decided > 0) {
    parts.push(tr(m.skipDecided, { count: result.skipped_decided }));
  }
  if (result.skipped_same_outcome > 0) {
    parts.push(tr(m.skipSame, { count: result.skipped_same_outcome }));
  }
  const head = tr(m.bulkHead, {
    applied: result.applied,
    total: result.obligations_total,
  });
  return parts.length === 0
    ? head
    : tr(m.bulkWithSkips, { head, skips: parts.join("; ") });
}

export interface QueueSplit {
  /** Ждут суждения: отчёт есть, проверки нет. */
  awaiting: number;
  /** Уже разобрано в этой работе. */
  decided: number;
  /** Контуры без отчёта — они НЕ ждут проверяющего. */
  noReport: number;
}

/**
 * Разложить строку очереди на три честных числа. «Без отчёта» держится
 * отдельно и не складывается с «ждут»: молчание — не задача проверяющего, и
 * превращать его в очередь значило бы требовать суждения там, где нет
 * предмета.
 */
export function queueSplit(row: VerificationQueueRowOut): QueueSplit {
  const at = (key: string) =>
    row.by_state.find((bucket) => bucket.key === key)?.count ?? 0;
  const noReport = at("no_data");
  return {
    awaiting: row.awaiting,
    decided: row.obligations.total - noReport - row.awaiting,
    noReport,
  };
}

export interface ClaimPair {
  /** Что сказало хозяйство. */
  reported: {
    progress: string | null;
    on: string | null;
    by: string | null;
    onBehalf: boolean;
  };
  /** Что сказал проверяющий — списком, а не одним «итогом». */
  verifications: readonly VerificationClaimOut[];
  /** Спор: есть опровержение при стоящем рядом отчёте. */
  disputed: boolean;
}

/**
 * Собрать спор в пару утверждений. Ни одно не выводится из другого и ни одно
 * не отменяет другое: если бы экран показывал «итог», он объявил бы спор
 * решённым, а он не решён.
 */
export function claimPair(row: {
  progress: string | null;
  reported_on: string | null;
  reported_by: string | null;
  entered_on_behalf: boolean;
  verifications: readonly VerificationClaimOut[];
}): ClaimPair {
  return {
    reported: {
      progress: row.progress,
      on: row.reported_on,
      by: row.reported_by,
      onBehalf: row.entered_on_behalf,
    },
    verifications: row.verifications,
    disputed:
      row.progress !== null &&
      row.verifications.some((claim) => claim.outcome === "rejected"),
  };
}

/* ------------------------------------------------------------------ */
/* Адрес строки очереди и разрез очереди по кампаниям                  */
/* ------------------------------------------------------------------ */

/**
 * Якорь строки очереди.
 *
 * Пятый уровень — работа — собственного адреса пока НЕ имеет: `/works/:id` не
 * заведён, и обещать его ссылкой было бы обещанием, которого некому выполнить
 * (`lib/levels.levelPath`). Пока его нет, адресуемой единицей работы остаётся
 * строка очереди: она единственное место, где по этой работе высказываются.
 *
 * Функция живёт здесь, а не в разметке, ровно потому, что её знают ДВА экрана:
 * очередь, которая ставит якорь, и кампания, которая на него ссылается. Две
 * копии строки `work-${id}` разошлись бы молча, и ссылка просто перестала бы
 * попадать в цель.
 */
export function queueAnchor(farmObligationId: number): string {
  return `work-${farmObligationId}`;
}

export interface QueueGroup {
  campaignId: number;
  campaignTitle: string;
  rows: VerificationQueueRowOut[];
  /** Сумма по строкам группы: одна работа принадлежит одной кампании. */
  obligations: { total: number; reported: number; verified: number };
  /** Сколько контуров группы ждут суждения. */
  awaiting: number;
  /**
   * Строки, у которых окно ещё не закончилось. У них отсутствие отчёта
   * означает «ещё не срок», а не «не сделано», и это надо сказать словами:
   * иначе идущая кампания читается в очереди как проваленная.
   */
  openWindows: number;
}

/**
 * Разрез очереди по кампаниям — маршрут разбора, а не новая сортировка.
 *
 * Сервер уже отсортировал очередь по вниманию, и переставлять строки внутри
 * группы нельзя: порядок «куда ехать первым» принадлежит ему. Поэтому группы
 * идут в порядке ПЕРВОГО появления своей кампании, а строки внутри — в том
 * порядке, в каком пришли. Никакого ранжирования кампаний между собой здесь
 * нет и быть не может: это была бы оценка кампаний, которой в корпусе нет.
 */
export function groupByCampaign(
  rows: ReadonlyArray<VerificationQueueRowOut>,
): QueueGroup[] {
  const order: number[] = [];
  const groups = new Map<number, QueueGroup>();

  for (const row of rows) {
    let group = groups.get(row.campaign_id);
    if (!group) {
      group = {
        campaignId: row.campaign_id,
        campaignTitle: row.campaign_title,
        rows: [],
        obligations: { total: 0, reported: 0, verified: 0 },
        awaiting: 0,
        openWindows: 0,
      };
      groups.set(row.campaign_id, group);
      order.push(row.campaign_id);
    }
    group.rows.push(row);
    group.obligations.total += row.obligations.total;
    group.obligations.reported += row.obligations.reported;
    group.obligations.verified += row.obligations.verified;
    group.awaiting += row.awaiting;
    if (row.window_elapsed === false) group.openWindows += 1;
  }

  return order.map((id) => groups.get(id) as QueueGroup);
}

/**
 * Почему в этой группе есть работы без отчёта — словами.
 *
 * «Нет отчёта» у идущего окна означает «ещё не срок». Разница не косметическая:
 * первое читается как невыполнение, второе — как календарь, и разбирать их
 * проверяющий должен по-разному.
 */
export function openWindowNote(group: QueueGroup): string | null {
  if (group.openWindows === 0) return null;
  return tr(m.openWindow, { count: group.openWindows });
}
